import { useMemo } from 'react'
import { Outlet } from 'react-router-dom'
import { MdOutlineInfo, MdOutlineSettings } from 'react-icons/md'
import { canAccess, signOut, useSession, type Permission } from '../../auth'
import { AppShell, isPortedArea, portalHref, type NavItem, type NavLinkItem } from '../../shell'
import { MENU, UPGRADE_PROCESS, type AreaLink } from '../areas'

function toLink(link: AreaLink): NavLinkItem {
  const Icon = link.icon
  const icon = Icon ? <Icon /> : undefined
  if (isPortedArea(link.area))
    return { id: link.path, label: link.label, href: link.path, ...(icon && { icon }) }
  // Unported area: the same react-admin path in the portal (ADR 0017).
  const href = portalHref(link.path)
  return href
    ? { id: link.path, label: link.label, href, isExternal: true, ...(icon && { icon }) }
    : { id: link.path, label: link.label, href: link.path, ...(icon && { icon }) }
}

/** The menu for these permissions (each item gated on `list`, as in the portal). */
export function buildNav(permissions: readonly Permission[]): NavItem[] {
  const may = (resource: string) => canAccess({ permissions, resource, action: 'list' })
  const items: NavItem[] = MENU.filter((link) => may(link.resource)).map(toLink)
  // The portal gates the whole Status group on upgradeprocess/tenants only.
  if (may('upgradeprocess/tenants')) {
    items.push({
      id: 'status',
      label: 'Status',
      title: 'Upgrade process',
      icon: <MdOutlineInfo />,
      children: UPGRADE_PROCESS.map(toLink)
    })
  }
  items.push({
    id: '/settings',
    label: 'Settings',
    href: '/settings',
    icon: <MdOutlineSettings />,
    isPinned: true
  })
  return items
}

/** The authenticated layout route: shell around the matched page. */
export function ShellLayout() {
  const { permissions, account } = useSession()
  const nav = useMemo(() => buildNav(permissions), [permissions])

  return (
    <AppShell
      nav={nav}
      version={import.meta.env.APP_VERSION}
      {...(account ? { user: { name: account.name } } : {})}
      onLogout={() => {
        void signOut({ reason: 'user' })
      }}
    >
      <Outlet />
    </AppShell>
  )
}
